/**
 * DP FAILURE WALK — DP101 chapter 3. Take her worst case and watch what it costs.
 *
 * FOUNDER, 2026-08-20: "walk along her a simulation".
 *
 * ── WHAT THE CHAPTER ASKS AND WHAT PAPER CANNOT ANSWER ────────────────────────────────────────
 * The chapter's sentence is that the worst-case failure "is the single failure that takes the most
 * capability with it", and that on a two-split diesel-electric vessel it is NORMALLY the loss of one
 * complete switchboard. Normally is doing real work in that sentence. So this bench computes it
 * rather than repeating it — and on her declared build the answer is not symmetric: losing PORT
 * costs far more than losing STBD, which is a fact about this vessel that no general rule contains.
 *
 * ── THE WALK IS ALONG THE SPLIT, NOT ALONG THE DECK ───────────────────────────────────────────
 * "Walk along her" is answered here as the walk that ch3 is actually about: from one switchboard,
 * through what hangs off it, to the other side — so the redundancy boundary stops being a diagram
 * and becomes a place with equipment on either side of it. A three-dimensional walk of her spaces
 * is a later phase and is not pretended at here; what is drawn is what her model declares.
 *
 * ── NO SCORE, AND THE REFUSAL IS THE COMPETENCY ───────────────────────────────────────────────
 * A drill that rewards holding station teaches the wrong reflex. The DPO competency in this chapter
 * is knowing which failure you are built against and what it leaves you — so what is shown after a
 * failure is the envelope that survives, including where it is gone entirely.
 */

import { useState, useEffect } from 'react';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, cited, refused, absent } from '../../../packages/ankr-provenance/src/provenance.mjs';
import DpWalkLab from './dpwalklab.jsx';
import {
  assemble, activeFailures, worstCase, windLimit,
  benchEnv, benchEnvWhy, WHY_NOT_SCORED, SOURCE_CH3, criterionOf, HS_FROM_WIND_NOTE,
  loadCore,
} from './dplabs.mjs';

export default function DpFailureLab() {
  const [model, setModel] = useState(null);
  const [core, setCore] = useState(null);
  const [sel, setSel] = useState('intact');
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    Promise.all([
      fetch('/api/dp/model').then((r) => r.json()),
      loadCore(),
    ])
      .then(([m, c]) => { if (live) { setModel(m); setCore(c); } })
      .catch((e) => live && setErr(String(e && e.message || e)));
    return () => { live = false; };
  }, []);

  if (err) return <div className="g-glass shell-panel lab"><p className="g-body"><Value of={refused(`The walk needs her declared model and the DP engine, and could not reach them: ${err}. Nothing is drawn rather than a stand-in vessel.`)} /></p></div>;
  if (!model || !core) return <div className="g-glass shell-panel lab"><p className="g-body g-dim">Loading her declared model and the engine…</p></div>;

  const built = assemble(model, [...(model.thrusters || []).map((t) => t.id), ...(model.generators || []).map((g) => g.id)]);
  const cands = activeFailures(built);
  const { worst, tied } = worstCase(built, core, model);
  const chosen = sel === 'intact' ? { id: 'intact', label: 'no failure', dead: [], deadGenerators: [] } : cands.find((c) => c.id === sel);

  const intactLimit = windLimit(built, core, model);
  const nowLimit = windLimit(built, core, model, chosen.dead, chosen.deadGenerators);
  const held = core.canHold(built, benchEnv(core, model), chosen.dead, chosen.deadGenerators);
  const boards = [...new Set([...(model.thrusters || []).map((t) => t.switchboard), ...(model.generators || []).map((g) => g.switchboard)])];
  const isDead = (u) => chosen.dead.includes(u.id) || chosen.deadGenerators.includes(u.id);
  /* A LOST CAPABILITY IS A SUBTRACTION, and it is only meaningful if both ends were measured the
     same way. Both limits come from the same sweep with the same current, so the difference is the
     failure and nothing else. If either end is null she could not hold at all, and a percentage of
     nothing would be a manufactured number. */
  const lost = (intactLimit === null || nowLimit === null) ? null : intactLimit - nowLimit;

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Failure walk · what one fault takes with it</div>
      <h2 className="g-h2 lab-h">Her worst case is not the biggest thruster.</h2>

      <p className="g-body">
        <Value of={cited('The worst-case failure is the single failure that takes the most capability with it. On a two-split diesel-electric vessel the worst case is normally the loss of one complete switchboard with its generators and its thrusters.', SOURCE_CH3)} />
      </p>
      <p className="g-body g-dim">
        <b>Normally</b> is doing the work in that sentence, so it is computed here rather than assumed.
      </p>

      {/* ── PICK A FAILURE ── */}
      <div className="lab-task">
        <div className="g-label">Break something on purpose</div>
        <div className="lab-task-head dpf-picks">
          <button className={`g-btn ${sel === 'intact' ? 'dpb-on' : ''}`} onClick={() => setSel('intact')}>intact</button>
          {cands.map((c) => (
            <button key={c.id} className={`g-btn ${sel === c.id ? 'dpb-on' : ''} ${worst && c.id === worst.id ? 'dpf-worst' : ''}`} onClick={() => setSel(c.id)}>
              {c.id.replace(/^(thruster|generator|switchboard):/, '')}
            </button>
          ))}
        </div>
        {worst && (
          <p className="g-body dpb-worst">
            <b>Computed worst case:</b> <Value of={computed(worst.label)} />
            {tied && tied.length > 1 && <> — {tied.length - 1} other failure(s) cost exactly the same.</>}
          </p>
        )}
      </div>

      {/* ── THE WALK IN THREE DIMENSIONS, WITH THE TWO-COLUMN SPLIT AS ITS FLOOR ──
          Added 2026-08-20 on the founder's "now do the 3d walk along her". It mounts ABOVE the
          split and never replaces it: FP-010 wants a zero-dependency floor, WebGL is missing from
          plenty of what is aboard a ship, and the split needs nothing but this page. If the walk
          cannot draw, it says why and the boundary is still here. */}
      <DpWalkLab model={model} dead={chosen.dead} deadGenerators={chosen.deadGenerators}
        failureLabel={chosen.id === 'intact' ? null : chosen.label} />

      {/* ── THE SAME BOUNDARY, FLAT ── */}
      <div className="lab-task">
        <div className="g-label">The redundancy boundary, with what hangs off each side</div>
        <div className="dpf-split">
          {boards.map((b) => (
            <div key={b} className="dpf-side">
              <b>{b}</b>
              {[...(model.thrusters || []), ...(model.generators || [])].filter((u) => u.switchboard === b).map((u) => (
                <div key={u.id} className={`dpf-unit ${isDead(u) ? 'dpf-dead' : ''}`}>
                  {u.id} <span className="g-dim">{u.type || 'generator'} · {u.kw} kW</span>
                  {isDead(u) && <span className="dpf-x"> — out</span>}
                </div>
              ))}
            </div>
          ))}
        </div>
        <p className="g-body g-dim">
          The boundary is not a line on a diagram, it is a place with equipment on either side of
          it. This flat view is the floor: it needs nothing but this page, and it says the same
          thing the walk above says with her actual geometry.
        </p>
      </div>

      {/* ── WHAT IT COST ── */}
      <div className="lab-task dpb-verdict">
        <div className="g-label">What that failure cost her</div>
        <p className="g-body">
          At the bench condition she{' '}
          {held.feasible
            ? <Value of={computed('holds')} />
            : <Value of={refused('loses position')} />}
        </p>
        <p className="g-body">
          Wind she still holds:{' '}
          {nowLimit === null
            ? <Value of={refused('none — she cannot hold even in still air')} />
            : <Value of={computed(`${nowLimit} kn`)} />}
          {intactLimit !== null && <span className="g-dim"> (intact: {intactLimit} kn)</span>}
        </p>
        {lost !== null && (
          <p className="g-body">
            Capability taken:{' '}
            <Value of={computed(`${lost} kn of wind`)} />
            {lost === 0 && <span className="g-dim"> — this failure costs her nothing at all in this condition</span>}
          </p>
        )}
        {held.binding && <p className="g-body g-dim">What binds her: {String(held.binding)}</p>}
        <p className="g-body g-dim">{benchEnvWhy(model)} {HS_FROM_WIND_NOTE}</p>
      </div>

      <p className="g-body g-dim lab-foot">
        {WHY_NOT_SCORED} Holding station is not the competency being practised here; knowing what you
        are built against, and what it leaves you, is.
      </p>
      <p className="g-body g-dim">
        <Value of={absent()} /> Nothing on this walk is an attestation.
      </p>
    </div>
  );
}
